import { DollarOutlined } from '@ant-design/icons';
import { Button, Modal, Popover } from 'antd';
import { memo, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import useAppSelector from 'hooks/useAppSelector';
import { BIDDING_ERROR, openBidding } from 'pages/bids/CreateBid/constants';
import ERR_MSG from 'utils/errorMessage';

import { Auction, isActiveAuction, isEndedAuction, isNotYetStartedAuction } from 'models/auction/Auction';

import CreateBidForm from 'pages/bids/CreateBid/CreateBidForm';
import styles from './BiddingButton.module.css';

interface BiddingButtonProps {
  auction: Auction;
  setAuction: (auction: Auction) => void;
  showShadow?: boolean;
}

function BiddingButton({ auction, setAuction, showShadow }: BiddingButtonProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showCreateBidForm, setShowCreateBidForm] = useState<boolean>(false);

  useEffect(() => {
    setShowCreateBidForm(searchParams.get(openBidding.key) === openBidding.value);
  }, [searchParams]);

  const { authenticated, loginUser } = useAppSelector((state) => state.user);
  const isLoginUserOwnAuction = loginUser?.id === auction.seller.id;
  const isDifferentMarketplace = loginUser && loginUser.marketplace_id !== auction.marketplace_id;
  const cannotBidding = isLoginUserOwnAuction || isDifferentMarketplace || !isActiveAuction(auction);

  let biddingErrMsg = ERR_MSG.UNKNOWN;
  if (isNotYetStartedAuction(auction)) biddingErrMsg = BIDDING_ERROR.IS_NOT_STARTED_AUCTION;
  else if (isEndedAuction(auction)) biddingErrMsg = BIDDING_ERROR.IS_ENDED_AUCTION;
  else if (isLoginUserOwnAuction) biddingErrMsg = BIDDING_ERROR.IS_LOGIN_USER_OWN_AUCTION;
  else if (isDifferentMarketplace) biddingErrMsg = BIDDING_ERROR.IS_DIFFERENT_MARKETPLACE;

  const onClickBiddingButton = () => {
    if (cannotBidding) return;
    setShowCreateBidForm(true);
  };

  const closeCreateBidForm = () => {
    if (searchParams.get(openBidding.key)) {
      searchParams.delete(openBidding.key);
      setSearchParams(searchParams, { replace: true });
    }
    setShowCreateBidForm(false);
  };

  useEffect(() => {
    if (showCreateBidForm && !authenticated) {
      Modal.confirm({
        content: '입찰을 위해 로그인이 필요합니다. 로그인하시겠습니까?',
        icon: null,
        onOk() {
          navigate('/auth/login');
        },
        onCancel() {
          setShowCreateBidForm(false);
        },
        centered: true,
        okText: '로그인 하러 가기',
        cancelText: '취소',
      });
    }
  }, [authenticated, navigate, showCreateBidForm]);

  const biddingButton = (
    <Button
      className={`${styles.btn} ${showShadow ? styles.shadow : ''} ${cannotBidding ? styles.disabled : ''}`}
      type="primary"
      block
      icon={<DollarOutlined />}
      onClick={onClickBiddingButton}
    >
      입찰하러 가기
    </Button>
  );

  return (
    <>
      {cannotBidding ? (
        <Popover trigger="hover" content={biddingErrMsg}>
          {biddingButton}
        </Popover>
      ) : (
        biddingButton
      )}
      <Modal
        title={null}
        footer={null}
        wrapClassName={styles.modal}
        centered
        open={authenticated && showCreateBidForm}
        onCancel={closeCreateBidForm}
      >
        <CreateBidForm auction={auction} setAuction={setAuction} closeCreateBidForm={closeCreateBidForm} />
      </Modal>
    </>
  );
}

export default memo(BiddingButton);
